import { CirclePlus, Crosshair, ChevronDown, ChevronUp } from "lucide-react"
import { useStore } from "zustand"

import { Button } from "@/components/button"
import { CardHeading } from "@/components/shared/card-heading"
import { Tag } from "@/components/shared/tag"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
} from "@/components/ui/card"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { useCreateTalentMutation } from "@/hooks/talent-config"
import {
  useTalentConfigRouteStore,
  setOpenTrack,
  updateNewTalentDraftField,
} from "@/lib/stores/talent-config-ui-store"
import {
  type TalentConfigBoss,
  type TalentCategory,
} from "@/lib/talent-config-api"
import { getNextTalentSlot } from "@/lib/utils/talent-config.utils"
import {
  isTalentDirty,
  validateTalentDraft,
} from "@/lib/utils/talent-draft.utils"

import { TierRow } from "./tier-row"

const CATEGORY_LABELS: Record<TalentCategory, string> = {
  offensive: "Offensive",
  defensive: "Defensive",
  strategic: "Strategic",
}

export function TrackSection({
  boss,
  category,
}: {
  boss: TalentConfigBoss
  category: TalentCategory
}) {
  const store = useTalentConfigRouteStore()
  const isOpen = useStore(store, (s) => s.openTracks[category])
  const newTalentDraft = useStore(store, (s) => s.newTalentDrafts[category])
  const talentDrafts = useStore(store, (s) => s.talentDrafts)

  const talents = boss.talentsByCategory[category]
  const nextSlot = getNextTalentSlot(boss, category)

  const dirtyCount = talents.filter((talent) =>
    isTalentDirty(talentDrafts[talent.id], talent),
  ).length

  // Initialize new talent draft if it doesn't exist
  const draft = newTalentDraft ?? {
    description: "",
    reward: "",
  }

  const createTalentMutation = useCreateTalentMutation(store)

  const newTalentDraftValidation = validateTalentDraft(draft)

  return (
    <Card id={getTrackSectionId(category)} className="gap-0">
      <Collapsible
        open={isOpen}
        onOpenChange={(open) => setOpenTrack(store, category, open)}
      >
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <CardHeading icon={<Crosshair className="size-5 text-primary" />}>
                {CATEGORY_LABELS[category]} Track
              </CardHeading>
              <CardDescription>
                Edit the player-facing requirement copy and reward text for each
                tier.
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Tag className="bg-card/70 text-[0.68rem]">
                {talents.length} tiers
              </Tag>
              <span className="rounded-full border border-warning/35 bg-warning/12 px-2.5 py-1 text-[0.68rem] font-semibold text-warning">
                {dirtyCount} unsaved
              </span>
              <CollapsibleTrigger className="inline-flex items-center gap-1.5 rounded-lg border border-border/70 bg-card/70 px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-card hover:text-foreground">
                {isOpen ? "Collapse" : "Expand"}
                {isOpen ? (
                  <ChevronUp className="size-3.5" />
                ) : (
                  <ChevronDown className="size-3.5" />
                )}
              </CollapsibleTrigger>
            </div>
          </div>
        </CardHeader>
        <CollapsibleContent>
          <CardContent className="grid gap-4">
            {talents.map((talent) => (
              <TierRow key={talent.id} talent={talent} />
            ))}

            {nextSlot ? (
              <div className="grid gap-3 rounded-[1.35rem] border border-dashed border-primary/25 bg-[color-mix(in_oklch,var(--primary)_4%,var(--card))] p-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-kicker text-xs text-muted-foreground">
                      Add Tier {nextSlot.tier}
                    </div>
                    <div className="mt-1 text-sm text-muted-foreground">
                      Create the next {CATEGORY_LABELS[category].toLowerCase()}{" "}
                      entry for this boss.
                    </div>
                  </div>
                  <Button
                    size="sm"
                    type="button"
                    disabled={
                      createTalentMutation.isPending ||
                      Boolean(
                        newTalentDraftValidation.description ||
                        newTalentDraftValidation.reward,
                      )
                    }
                    onClick={() => {
                      createTalentMutation.mutate({
                        boss,
                        category,
                        draft,
                      })
                    }}
                  >
                    <CirclePlus className="size-3.5" />
                    {createTalentMutation.isPending
                      ? "Creating..."
                      : "Add Talent"}
                  </Button>
                </div>

                <div className="grid gap-2">
                  <Label htmlFor={`${boss.id}-${category}-new-description`}>
                    Requirement
                  </Label>
                  <Textarea
                    id={`${boss.id}-${category}-new-description`}
                    value={draft.description}
                    onChange={(event) =>
                      updateNewTalentDraftField(
                        store,
                        category,
                        "description",
                        event.target.value,
                      )
                    }
                  />
                  {newTalentDraftValidation.description ? (
                    <p className="text-xs text-destructive">
                      {newTalentDraftValidation.description}
                    </p>
                  ) : null}
                </div>

                <div className="grid gap-2">
                  <Label htmlFor={`${boss.id}-${category}-new-reward`}>
                    Reward
                  </Label>
                  <Input
                    id={`${boss.id}-${category}-new-reward`}
                    value={draft.reward}
                    onChange={(event) =>
                      updateNewTalentDraftField(
                        store,
                        category,
                        "reward",
                        event.target.value,
                      )
                    }
                  />
                  {newTalentDraftValidation.reward ? (
                    <p className="text-xs text-destructive">
                      {newTalentDraftValidation.reward}
                    </p>
                  ) : null}
                </div>
              </div>
            ) : (
              <div className="rounded-[1.2rem] border border-dashed border-border/70 px-4 py-3 text-sm text-muted-foreground">
                All {CATEGORY_LABELS[category].toLowerCase()} slots are filled.
              </div>
            )}
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  )
}

function getTrackSectionId(category: TalentCategory): string {
  return `talent-track-${category}`
}
